import type { ClassId } from '../../types/progression'

/**
 * Imágenes de clases, NPC y monstruos, asociadas al ID exacto de su entidad.
 *
 * - Una imagen de clase corresponde solo a la clase inicial; sus evoluciones necesitan su propia entrada.
 * - `origin: 'staff'` = "Imagen proporcionada por el staff". Esto identifica a la entidad; no confirma drops,
 *   coordenadas, estadísticas ni recetas.
 * - `matchedBy` explica cómo se asoció cada archivo (las subidas llegaron como "image.png", sin el nombre
 *   clase_/npc_/monstruo_), para que el staff pueda revisar la correspondencia.
 */
export type ImageOrigin = 'staff'

export type EntityImage = {
  src: string
  origin: ImageOrigin
  addedOn: string
  /** Cómo se asoció el archivo a la entidad. */
  matchedBy: string
  /** Asociación que el staff debe confirmar antes de darla por segura. */
  needsConfirmation?: boolean
}

export type ImageEntryKind = 'clase' | 'npc' | 'monstruo'

export type ImageEntry = {
  key: string
  label: string
  kind: ImageEntryKind
  classId?: ClassId
  /** ID exacto en 06_monsters (NPC y monstruos). */
  monsterId?: number
  /** Solo si el ID no está fijado: se resuelve por nombre exacto y únicamente con una sola coincidencia. */
  monsterName?: string
  image?: EntityImage
  pending?: string
}

const DATE = '10/10/2026'
const staff = (src: string, matchedBy: string, needsConfirmation = false): EntityImage => ({ src, origin: 'staff', addedOn: DATE, matchedBy, needsConfirmation })
const CDN = 'https://cdn.magicpatterns.com/uploads'
const ORDER = 'Asociada por el orden de envío (llegó como "image.png", sin nombre de entidad) y por su aspecto'
const LABEL = 'El nombre aparece escrito sobre el personaje en la captura'

export const entityImages: ImageEntry[] = [
  { key: 'clase:dk', label: 'Dark Knight', kind: 'clase', classId: 'dk', image: staff(`${CDN}/gaBrfPnk1L7URyjrXSM7CQ/image.png`, ORDER) },
  { key: 'clase:dw', label: 'Dark Wizard', kind: 'clase', classId: 'dw', image: staff(`${CDN}/ax84a7DyPr3veMQFyLEy7u/image.png`, ORDER) },
  { key: 'clase:elf', label: 'Fairy Elf', kind: 'clase', classId: 'elf', image: staff(`${CDN}/i39j9RAhcwDGGymp3NFTRS/image.png`, ORDER) },
  { key: 'clase:sum', label: 'Summoner', kind: 'clase', classId: 'sum', image: staff(`${CDN}/qfDwtaX1UsuNWhKajrWuRR/image.png`, ORDER) },
  { key: 'clase:mg', label: 'Magic Gladiator', kind: 'clase', classId: 'mg', image: staff(`${CDN}/5RwLn6PAkky5YJQNcPtneX/image.png`, ORDER) },
  { key: 'clase:dl', label: 'Dark Lord', kind: 'clase', classId: 'dl', image: staff(`${CDN}/getDimKHRgAcd4v4te3uwQ/image.png`, ORDER) },
  { key: 'clase:rf', label: 'Rage Fighter', kind: 'clase', classId: 'rf', image: staff(`${CDN}/47ApDiNqN7ycDUXSeUWRy8/image.png`, ORDER) },
  { key: 'monstruo:235', label: 'Sebina the Priest', kind: 'npc', monsterId: 235, image: staff(`${CDN}/egoa1woT4kUUkuUTw287Gq/image.png`, LABEL) },
  { key: 'monstruo:229', label: 'Marlon', kind: 'npc', monsterId: 229, image: staff(`${CDN}/roRaYMZ2vWknrCTxZVik6E/image.png`, LABEL) },
  { key: 'monstruo:238', label: 'Chaos Goblin', kind: 'npc', monsterId: 238, image: staff(`${CDN}/haQXk78phhw5G3hSN6aE6y/image.png`, LABEL) },
  { key: 'monstruo:406', label: 'Apostle Devin', kind: 'npc', monsterId: 406, image: staff(`${CDN}/r8pj5tGyBkHA4YWwfC2wBC/image.png`, `${ORDER}; la captura no muestra el nombre`, true) },
  { key: 'monstruo:407', label: 'Werewolf Quarel', kind: 'npc', monsterId: 407, image: staff(`${CDN}/mfoD2a3bjLmYUpPhimRsyi/image.png`, `${ORDER}; la captura no muestra el nombre`, true) },
  { key: 'monstruo:408', label: 'Gatekeeper', kind: 'npc', monsterId: 408, image: staff(`${CDN}/coawYWAxLawv2ruqPXM2MC/image.png`, `${ORDER}; puerta con rostro, como el Gatekeeper`) },
  { key: 'monstruo:226', label: 'Trainer', kind: 'npc', monsterId: 226, image: staff(`${CDN}/qgDdKd5yFZPqQofCQB52rv/image.png`, 'Confirmada por el staff: es el Trainer (el mismo NPC que "Pet Trainer")') },
  { key: 'monstruo:42', label: 'Red Dragon', kind: 'monstruo', monsterId: 42, image: staff(`${CDN}/jfCUkdtkrTmKZhsnLzpYxD/image.png`, ORDER) },
  { key: 'nombre:white-wizard', label: 'White Wizard', kind: 'monstruo', monsterName: 'White Wizard', image: staff(`${CDN}/ipPu2ABaGiysedUUVHiiT2/image.png`, ORDER) },
  { key: 'nombre:death-king', label: 'Death King', kind: 'monstruo', monsterName: 'Death King', image: staff(`${CDN}/vHwLvbeqi4y8GDfAodkGzT/image.png`, LABEL) },
]

/** Imágenes recibidas sin identificación suficiente: no se muestran a los jugadores. */
export const unassignedImages: { src: string; description: string; question: string }[] = []
